import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import PageHeader from '../../components/PageHeader';
import DataTable, { Column } from '../../components/DataTable';
import StatusBadge from '../../components/StatusBadge';
import { adminApi } from '../../services/admin';
import type { AdminRuleTriggerLog, ScoringCycle } from '../../types';


type ReviewAction = 'confirm' | 'override' | 'adjust';


function ReviewBadge({ status }: { status: AdminRuleTriggerLog['review_status'] }) {
  if (status === 'pending') return <StatusBadge label="Pending Review" variant="warning" />;
  if (status === 'confirmed') return <StatusBadge label="Confirmed" variant="success" />;
  if (status === 'overridden') return <StatusBadge label="Overridden" variant="error" />;
  if (status === 'adjusted') return <StatusBadge label="Adjusted" variant="warning" />;
  return null;
}


function ReviewModal({
  trigger, onClose,
}: { trigger: AdminRuleTriggerLog; onClose: () => void }) {
  const qc = useQueryClient();
  const [action, setAction] = useState<ReviewAction>('confirm');
  const [reason, setReason] = useState('');
  const [adjusted, setAdjusted] = useState<string>(String(trigger.score_impact));

  const reviewMutation = useMutation({
    mutationFn: () => adminApi.reviewTrigger(trigger.id, {
      action,
      reason: reason || undefined,
      score_impact_adjusted: action === 'adjust' ? Number(adjusted) : undefined,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'rule-triggers'] });
      onClose();
    },
  });

  const reasonRequired = action === 'override' || action === 'adjust';
  const submitDisabled = reviewMutation.isPending || (reasonRequired && !reason.trim());

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full p-6" onClick={(e) => e.stopPropagation()}>
        <h2 className="text-lg font-semibold text-slate-900">Review Critical Trigger</h2>
        <p className="mt-1 text-sm text-slate-500">
          {trigger.rule_name} &middot; {trigger.entity_type} &middot; impact{' '}
          <span className="font-semibold text-red-600">
            {Number(trigger.score_impact).toFixed(1)}
          </span>
        </p>
        <p className="mt-3 text-xs text-slate-600 line-clamp-3">{trigger.explanation}</p>

        <div className="mt-5 space-y-3">
          <div className="flex gap-2">
            {(['confirm', 'override', 'adjust'] as ReviewAction[]).map(a => (
              <button
                key={a}
                onClick={() => setAction(a)}
                className={`flex-1 py-2 text-sm font-medium rounded-lg capitalize ${
                  action === a
                    ? 'bg-brand-500 text-white'
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
              >
                {a}
              </button>
            ))}
          </div>

          {action === 'adjust' && (
            <label className="block">
              <span className="text-xs font-medium text-slate-600">
                Adjusted score impact (e.g. -10)
              </span>
              <input
                type="number"
                step="0.5"
                value={adjusted}
                onChange={(e) => setAdjusted(e.target.value)}
                className="mt-1 w-full rounded-lg border-2 border-slate-200 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none"
              />
            </label>
          )}

          <label className="block">
            <span className="text-xs font-medium text-slate-600">
              Reason {reasonRequired ? '(required)' : '(optional)'}
            </span>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              className="mt-1 w-full rounded-lg border-2 border-slate-200 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none"
              placeholder="Why are you taking this action?"
            />
          </label>

          {reviewMutation.isError && (
            <p className="text-xs text-red-600">
              {(reviewMutation.error as Error)?.message ?? 'Review failed'}
            </p>
          )}
        </div>

        <div className="mt-6 flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium rounded-lg text-slate-700 hover:bg-slate-100"
          >
            Cancel
          </button>
          <button
            onClick={() => reviewMutation.mutate()}
            disabled={submitDisabled}
            className="px-4 py-2 text-sm font-medium rounded-lg bg-brand-500 text-white disabled:opacity-50 hover:bg-brand-600"
          >
            {reviewMutation.isPending ? 'Submitting…' : 'Submit'}
          </button>
        </div>
      </div>
    </div>
  );
}


const cycleColumns: Column<ScoringCycle>[] = [
  { key: 'week_start', header: 'Week Start' },
  { key: 'week_end', header: 'Week End' },
  { key: 'status', header: 'Status', render: (r) => (
    <StatusBadge label={r.status} variant={r.status === 'completed' ? 'success' : r.status === 'running' ? 'warning' : 'error'} />
  )},
  { key: 'triggered_by', header: 'Triggered By', render: (r) => <span className="capitalize">{r.triggered_by}</span> },
  { key: 'total_guards_scored', header: 'Guards' },
  { key: 'total_sites_scored', header: 'Sites' },
  { key: 'total_rules_triggered', header: 'Triggers', render: (r) => `${r.total_rules_triggered} / ${r.total_rules_evaluated}` },
  { key: 'completed_at', header: 'Completed', render: (r) =>
    r.completed_at ? new Date(r.completed_at).toLocaleString() : '—'
  },
];

export default function AdminAudit() {
  const [tab, setTab] = useState<'triggers' | 'cycles'>('triggers');
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [entityTypeFilter, setEntityTypeFilter] = useState('');
  const [reviewStatusFilter, setReviewStatusFilter] = useState('');
  const [activeReview, setActiveReview] = useState<AdminRuleTriggerLog | null>(null);

  const { data: triggers, isLoading: triggersLoading } = useQuery({
    queryKey: ['admin', 'rule-triggers', page, search, entityTypeFilter, reviewStatusFilter],
    queryFn: () => adminApi.getRuleTriggers({
      page: String(page),
      search: search || undefined,
      entity_type: entityTypeFilter || undefined,
      review_status: reviewStatusFilter || undefined,
    }),
    enabled: tab === 'triggers',
  });

  const { data: cycles, isLoading: cyclesLoading } = useQuery({
    queryKey: ['admin', 'scoring-cycles', page],
    queryFn: () => adminApi.getScoringCycles({ page: String(page) }),
    enabled: tab === 'cycles',
  });

  const totalPages = tab === 'triggers'
    ? (triggers ? Math.ceil(triggers.count / 20) : 1)
    : (cycles ? Math.ceil(cycles.count / 20) : 1);

  // Feature 7: dynamic columns so the Action column is hidden in non-review modes.
  const triggerColumns: Column<AdminRuleTriggerLog>[] = [
    { key: 'scoring_cycle_week', header: 'Week' },
    { key: 'rule_name', header: 'Rule', render: (r) => (
      <div className="flex items-center gap-2">
        <span>{r.rule_name}</span>
        {r.review_status !== 'not_required' && <ReviewBadge status={r.review_status} />}
      </div>
    )},
    { key: 'rule_category', header: 'Category', render: (r) => <span className="capitalize">{r.rule_category.replace(/_/g, ' ')}</span> },
    { key: 'entity_type', header: 'Entity', render: (r) => <span className="capitalize">{r.entity_type}</span> },
    { key: 'score_impact', header: 'Impact', render: (r) => (
      <span className={`font-semibold ${r.is_positive ? 'text-emerald-600' : 'text-red-600'}`}>
        {r.is_positive ? '+' : ''}{Number(r.score_impact).toFixed(1)}
      </span>
    )},
    { key: 'score_before', header: 'Before', render: (r) => Number(r.score_before).toFixed(1) },
    { key: 'explanation', header: 'Explanation', render: (r) => (
      <span className="text-xs text-slate-500 line-clamp-2">{r.explanation}</span>
    )},
    { key: 'actions', header: '', render: (r) => (
      r.review_status === 'pending' ? (
        <button
          onClick={() => setActiveReview(r)}
          className="text-xs font-medium rounded-lg bg-brand-500 px-3 py-1 text-white hover:bg-brand-600"
        >
          Review
        </button>
      ) : null
    )},
  ];

  return (
    <>
      <PageHeader title="Audit" description="Rule triggers and scoring cycle history" />

      {/* Tab Switcher */}
      <div className="flex gap-1 mb-6 bg-slate-100 rounded-xl p-1 w-fit">
        <button
          onClick={() => { setTab('triggers'); setPage(1); setSearch(''); setEntityTypeFilter(''); setReviewStatusFilter(''); }}
          className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${tab === 'triggers' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
        >
          Rule Triggers
        </button>
        <button
          onClick={() => { setTab('cycles'); setPage(1); setSearch(''); setEntityTypeFilter(''); setReviewStatusFilter(''); }}
          className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${tab === 'cycles' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
        >
          Scoring Cycles
        </button>
      </div>

      {tab === 'triggers' && (
        <>
          <div className="flex flex-col sm:flex-row gap-3 mb-4">
            <input
              type="text"
              placeholder="Search triggers..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              className="w-full sm:w-80 rounded-xl border-2 border-slate-200 px-4 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
            />
            <select
              value={entityTypeFilter}
              onChange={(e) => { setEntityTypeFilter(e.target.value); setPage(1); }}
              className="rounded-xl border-2 border-slate-200 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none"
            >
              <option value="">All Entities</option>
              <option value="guard">Guard</option>
              <option value="site">Site</option>
              <option value="vendor">Vendor</option>
            </select>
            <select
              value={reviewStatusFilter}
              onChange={(e) => { setReviewStatusFilter(e.target.value); setPage(1); }}
              className="rounded-xl border-2 border-slate-200 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none"
            >
              <option value="">All Review States</option>
              <option value="pending">Pending Review</option>
              <option value="confirmed">Confirmed</option>
              <option value="overridden">Overridden</option>
              <option value="adjusted">Adjusted</option>
              <option value="not_required">Not Required</option>
            </select>
          </div>
          <DataTable
            columns={triggerColumns}
            data={triggers?.results ?? []}
            keyExtractor={(r) => r.id}
            isLoading={triggersLoading}
            emptyTitle="No rule triggers"
            page={page}
            totalPages={totalPages}
            onPageChange={setPage}
          />
        </>
      )}

      {tab === 'cycles' && (
        <DataTable
          columns={cycleColumns}
          data={cycles?.results ?? []}
          keyExtractor={(r) => r.id}
          isLoading={cyclesLoading}
          emptyTitle="No scoring cycles"
          page={page}
          totalPages={totalPages}
          onPageChange={setPage}
        />
      )}

      {activeReview && (
        <ReviewModal trigger={activeReview} onClose={() => setActiveReview(null)} />
      )}
    </>
  );
}
